import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import CheckIcon from '@mui/icons-material/Check';
import { Popover } from '@mui/material';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { VoiceDto } from '@shared/services/apiService/apiService';
import {
  resolveVoiceProviderDisplayName,
  voiceMatchesProviderQuery,
} from '@shared/utils/voiceDisplay';

import { useStyles } from './styles';

interface VoicePickerProps {
  voices: VoiceDto[] | undefined;
  selectedVoiceId?: string;
  disabled?: boolean;
  placeholder: string;
  onChange: (voiceId: string) => void;
}

export const VoicePicker: React.FC<VoicePickerProps> = ({
  voices,
  selectedVoiceId,
  disabled,
  placeholder,
  onChange,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedVoice = useMemo(
    () => voices?.find((voice) => voice.id === selectedVoiceId),
    [voices, selectedVoiceId],
  );

  const filteredVoices = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return voices ?? [];
    return (voices ?? []).filter((voice) => {
      const name = voice.name.toLowerCase();
      return name.includes(q) || voiceMatchesProviderQuery(voice, q);
    });
  }, [voices, query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    window.requestAnimationFrame(() => searchRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (filteredVoices.length === 0) {
      setActiveIndex(-1);
      return;
    }
    if (activeIndex >= filteredVoices.length || activeIndex < 0) {
      setActiveIndex(0);
    }
  }, [filteredVoices, activeIndex]);

  const selectVoice = (voiceId: string) => {
    onChange(voiceId);
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!filteredVoices.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % filteredVoices.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev - 1 + filteredVoices.length) % filteredVoices.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (activeIndex >= 0) selectVoice(filteredVoices[activeIndex].id);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        className={classNames(classes.trigger, {
          [classes.triggerPlaceholder]: !selectedVoice?.name,
        })}
        onClick={() => setOpen(true)}>
        {selectedVoice?.name || placeholder}
      </button>

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { className: classes.popoverPaper } }}
        onKeyDown={onKeyDown}>
        <div className={classes.searchHeader}>
          <IconSearch size={16} />
          <input
            ref={searchRef}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls="voice-picker-listbox"
            aria-autocomplete="list"
            className={classes.searchInput}
            placeholder={t('campaignPage.voicePicker.searchPlaceholder') as string}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query && (
            <button
              type="button"
              className={classes.searchClear}
              onClick={() => setQuery('')}
              aria-label={t('campaignPage.voicePicker.clearSearch') as string}>
              <IconX size={14} />
            </button>
          )}
        </div>
        <div id="voice-picker-listbox" role="listbox" className={classes.list}>
          {filteredVoices.length === 0 ? (
            <div className={classes.empty}>{t('campaignPage.voicePicker.empty')}</div>
          ) : (
            filteredVoices.map((voice, idx) => {
              const isSelected = selectedVoiceId === voice.id;
              const isActive = activeIndex === idx;
              return (
                <button
                  key={voice.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={classNames(classes.row, {
                    [classes.rowActive]: isActive && !isSelected,
                    [classes.rowSelected]: isSelected,
                  })}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => selectVoice(voice.id)}>
                  <span className={classes.rowTitle}>{voice.name}</span>
                  <span className={classes.providerBadge}>
                    {resolveVoiceProviderDisplayName(voice)}
                  </span>
                  {isSelected && <CheckIcon className={classes.checkIcon} />}
                </button>
              );
            })
          )}
        </div>
      </Popover>
    </>
  );
};
